import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { trackEvent } from 'user-analytics';
import { useDispatch, useSelector } from 'react-redux';
import { setAlert } from '@/actions';
import { setFrequentPassengers as setFrequentPassengersAction } from '@/actions/userPreferences';
import useUserPreferences from 'hooks/useUserPreferences';
import { normalizeFrequentPassengersData } from 'utils/normalizeFrequentPassengersData';
import userPreferencesService from 'services/userPreferences';
import { change } from 'redux-form';
import FrequentPassengersContext from './FrequentPassengersContext';
/**
 * FrequentPassengersProvider component.
 * Manages state and operations for frequent passengers functionality.
 * @param {object} props - children.
 * @param {React.ReactNode} props.children - Child components to be wrapped.
 */
const FrequentPassengersProvider = ({ children }) => {
  const { passengers } = useUserPreferences();
  const formPassengersState = useSelector((state) => state.form.passengers.values.passengers);
  const busCategories = useSelector((state) => state.purchase.get('busCategories'));
  const dispatch = useDispatch();
  const adaptedPassengers = normalizeFrequentPassengersData(passengers).map((passenger) => {
    return {
      ...passenger,
      selectedForPassenger: null,
      isSelected: false,
      isFrequentPassenger: false,
      preferredPassengerOrder: false,
    };
  });
  const [frequentPassengers, setFrequentPassengers] = useState(adaptedPassengers);
  const hasHydratedPassengers = useRef(Boolean(adaptedPassengers.length));
  const frequentPassengersRef = useRef();
  frequentPassengersRef.current = frequentPassengers;
  const { t } = useTranslation();
  /*
   * The array looks like this:
   * [
   *  {
   *    passenger: {} // frequent passenger object
   *    timeoutId,
   *  },
   * ]
   */
  const pendingPassengerDeletions = useRef([]);

  const trackFrequentPassengerSelected = (passenger, from) => {
    trackEvent('Frequent Passenger Selected', {
      Passenger: {
        'First Name': passenger.firstName,
        'Last Name': passenger.lastName,
        'Email': passenger.email || 'N/A',
        'Passenger Number': passenger.selectedForPassenger,
      },
      From: from,
    });
  };

  useEffect(() => {
    const currentRoute = window.location.pathname;
    const isPassengersRoute = currentRoute.includes('/passengers');
    if (!isPassengersRoute) {
      return;
    }
    if (frequentPassengers.length) {
      trackEvent('Frequent Passengers Showed', {
        'Passengers Count': frequentPassengers.length,
        'Passengers': frequentPassengers.map((passenger) => ({
          'First Name': passenger.firstName,
          'Last Name': passenger.lastName,
          'Email': passenger.email || 'N/A',
          'Passenger Number': passenger.selectedForPassenger,
          'Selected': passenger.isSelected,
        })),
      });
    }
    dispatch(setFrequentPassengersAction(adaptedPassengers));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!hasHydratedPassengers.current && adaptedPassengers.length) {
      hasHydratedPassengers.current = true;
      setFrequentPassengers(adaptedPassengers);
      dispatch(setFrequentPassengersAction(adaptedPassengers));
    }
    // Only hydrate an initially empty provider when the async recommendations arrive.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passengers]);

  const removePendingPassengerDeletion = (id) => {
    pendingPassengerDeletions.current = pendingPassengerDeletions.current.filter(
      (deletion) => deletion.passenger.id !== id,
    );
  };

  const undoDeleteFrequentPassenger = (id) => {
    const deletion = pendingPassengerDeletions.current.find(
      (deletion) => deletion.passenger.id === id,
    );
    clearTimeout(deletion.timeoutId);
    /**
     * Since this method is called from a timeout, we need to use the ref to have access to the latest state.
     * Otherwise, we would be using the stale state from when the timeout was set.
     */
    setFrequentPassengers([...frequentPassengersRef.current, deletion.passenger]);
    trackEvent('Frequent Passenger Delete Undone', {
      Passenger: {
        'First Name': deletion.passenger.firstName,
        'Last Name': deletion.passenger.lastName,
        'Email': deletion.passenger.email || 'N/A',
      },
    });
    removePendingPassengerDeletion(id);
  };

  /**
   * Removes a frequent passenger from the list.
   * @param {string} id - ID of the passenger to remove.
   */
  const removeFrequentPassenger = (id) => {
    const passengerToDelete = frequentPassengers.find((passenger) => passenger.id === id);
    setFrequentPassengers(frequentPassengers.filter((passenger) => passenger.id !== id));
    trackEvent('Frequent Passenger Deleted', {
      'Passenger': {
        'First Name': passengerToDelete.firstName,
        'Last Name': passengerToDelete.lastName,
        'Email': passengerToDelete.email || 'N/A',
      },
      'Passenger Number': passengerToDelete.selectedForPassenger,
      'Selected': passengerToDelete.isSelected,
    });
    pendingPassengerDeletions.current.push({
      passenger: passengerToDelete,
      timeoutId: setTimeout(async () => {
        try {
          await userPreferencesService.deleteFrequentPassenger(id);
          removePendingPassengerDeletion(id);
          /**
           * Update store with the latest state.
           */
          dispatch(setFrequentPassengersAction(frequentPassengersRef.current));
        } catch (error) {
          undoDeleteFrequentPassenger(id);
          dispatch(setAlert('', 'error', t('errors:could_not_delete_frequent_passenger')));
        }
      }, 10000),
    });
  };

  /**
   * Selects a frequent passenger for a specific passenger index.
   * @param {string} id - ID of the frequent passenger to select.
   * @param {number} passengerIndex - Index of the passenger to apply the selection to.
   */
  const selectFrequentPassenger = useCallback(
    (id, passengerIndex, from = '') => {
      const targetPassenger = frequentPassengers.find((p) => p.id === id);

      const newFrequentPassengers = frequentPassengers.map((passenger) => {
        if (passenger.id === id) {
          return {
            ...passenger,
            name: `${passenger.firstName} ${passenger.lastName}`,
            nationality: passenger.isoCountryCode || null,
            isSelected: true,
            selectedForPassenger: passengerIndex,
            isFrequentPassenger: true,
            preferredPassengerOrder: passengerIndex - 1 === frequentPassengers.indexOf(passenger),
          };
        }
        if (
          passenger.selectedForPassenger === passengerIndex ||
          (targetPassenger.isSelected &&
            passenger.selectedForPassenger === targetPassenger.selectedForPassenger)
        ) {
          return {
            ...passenger,
            nationality: null,
            isSelected: false,
            selectedForPassenger: null,
            isFrequentPassenger: false,
            preferredPassengerOrder: false,
          };
        }
        return passenger;
      });

      setFrequentPassengers(newFrequentPassengers);
      dispatch(setFrequentPassengersAction(newFrequentPassengers));
      const selectedPassenger = newFrequentPassengers.find((passenger) => passenger.id === id);
      trackFrequentPassengerSelected(selectedPassenger, from);

      if (selectedPassenger) {
        if (targetPassenger.isSelected) {
          const previousPassenger = formPassengersState[targetPassenger.selectedForPassenger - 1];
          if (previousPassenger) {
            const fieldsToKeep = {
              busCategory: previousPassenger.busCategory,
              category: previousPassenger.category,
              phoneCode: previousPassenger.phoneCode,
              phoneCountry: previousPassenger.phoneCountry,
              id: previousPassenger.id,
            };

            dispatch(
              change('passengers', `passengers[${targetPassenger.selectedForPassenger - 1}]`, {
                ...previousPassenger,
                ...fieldsToKeep,
                name: '',
                isFrequentPassenger: false,
                preferredPassengerOrder: false,
                selectedForPassenger: null,
                nationalityId: 1,
                nationality: null,
                firstName: '',
                lastName: '',
                email: '',
                secondFirstName: '',
                secondLastName: '',
                documentId: '',
                documentType: '',
                phone: '',
                gender: null,
                dateOfBirth: null,
              }),
            );
          }
        }

        // Set the passenger in the new position
        // Minor passengers cannot be the first (they can't travel alone)
        const isMinorAlone = selectedPassenger.busCategory === 'minor' && passengerIndex === 1;
        const isValidBusCategory =
          !isMinorAlone &&
          busCategories?.some(({ type }) => type === selectedPassenger.busCategory);
        dispatch(
          change('passengers', `passengers[${passengerIndex - 1}]`, {
            ...formPassengersState[passengerIndex - 1],
            ...selectedPassenger,
            id: formPassengersState[passengerIndex - 1].id,
            busCategory: isValidBusCategory
              ? selectedPassenger.busCategory
              : formPassengersState[passengerIndex - 1].busCategory,
          }),
        );
      }
    },
    [busCategories, dispatch, formPassengersState, frequentPassengers],
  );

  useEffect(() => {
    setFrequentPassengers((prevState) => {
      let didResetPassenger = false;
      const nextState = prevState.map((passenger) => {
        const formPassenger = formPassengersState[passenger.selectedForPassenger - 1];
        const shouldReset =
          passenger.isSelected &&
          (!formPassenger ||
            formPassenger.firstName !== passenger.firstName ||
            formPassenger.lastName !== passenger.lastName);

        if (!shouldReset) return passenger;

        didResetPassenger = true;
        return {
          ...passenger,
          name: `${passenger.firstName} ${passenger.lastName}`,
          isSelected: false,
          selectedForPassenger: null,
          isFrequentPassenger: false,
          preferredPassengerOrder: false,
        };
      });

      return didResetPassenger ? nextState : prevState;
    });
  }, [formPassengersState]);
  const contextValue = {
    frequentPassengers,
    removeFrequentPassenger,
    selectFrequentPassenger,
    pendingPassengerDeletions,
    undoDeleteFrequentPassenger,
  };
  return (
    <FrequentPassengersContext.Provider value={contextValue}>
      {children}
    </FrequentPassengersContext.Provider>
  );
};

FrequentPassengersProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default FrequentPassengersProvider;
